/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ReactNode } from "react";
import type { DefaultValues, UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";

import { spacing } from "../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { Form } from "../forms/wrapper/Form.emotion";
import { AuthLayout } from "./AuthLayout.emotion";

const containerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: spacing.ml,
  justifyContent: "flex-start",
  padding: spacing.ml,
  width: spacing.full,
  [cssMediaQueryMinWidthTablet]: { justifyContent: "space-between" },
};

type AuthForm<T> = {
  onSubmit: (data: T) => void;
  children: (
    form: UseFormReturn<T, any, undefined>,
    disabled: boolean,
  ) => ReactNode;
  form?: UseFormReturn<T, any, undefined>;
  schema?: any;
  defaultValues?: DefaultValues<T>;
  footer?: (
    form: UseFormReturn<T, any, undefined>,
    disabled: boolean,
  ) => ReactNode;
  footerCSS?: Interpolation;
  layoutCSS?: Interpolation;
  className?: string;
};

export const AuthForm = <T,>({
  children,
  schema,
  defaultValues,
  onSubmit,
  form: defaultForm,
  footerCSS = {},
  footer,
  layoutCSS = {},
  className = "",
}: AuthForm<T>) => {
  const form =
    defaultForm ??
    useForm<T>({
      defaultValues,
      resolver: zodResolver(schema),
    });
  const { isDirty, isValid, isSubmitting } = form.formState;

  return (
    <Form
      formCSS={containerStyle}
      formProps={{
        onSubmit: form.handleSubmit(onSubmit),
        className,
      }}
      {...form}
    >
      <AuthLayout
        css={layoutCSS}
        footer={footer(form, !isDirty || !isValid || isSubmitting)}
        footerCSS={footerCSS}
      >
        {children(form, !isDirty || !isValid || isSubmitting)}
      </AuthLayout>
    </Form>
  );
};

AuthForm.displayName = "AuthForm";
